import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, 
  Sparkles, 
  ClipboardPaste, 
  PenLine, 
  Wand2, 
  ArrowRight,
  X,
  Upload,
  AlertCircle
} from 'lucide-react';
import { handleRecipeCreationRequest } from '@/services/recipeService';
import RecipeParsingProgress from './RecipeParsingProgress';
import ParsedRecipePreview from './ParsedRecipePreview';
import { RecipeFormData } from '@/types/recipe';
import { readPendingRecipe, writePendingRecipe } from '@/lib/pendingRecipe';

type ViewState = 'launcher' | 'parsing' | 'preview';

// Accepted file types for upload
const ACCEPTED_FILE_TYPES = [
  'text/plain',
  'text/markdown', 
  'text/html',
  'application/pdf', // For future PDF support
];
const ACCEPTED_EXTENSIONS = ['.txt', '.md', '.markdown', '.html', '.htm'];
const ACCEPTED_DISPLAY = 'TXT, MD, HTML files';

const RecipeCreationLauncher: React.FC = () => {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<ViewState>('launcher');
  const [pastedText, setPastedText] = useState('');
  const [parsedRecipe, setParsedRecipe] = useState<Partial<RecipeFormData> | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showTextarea, setShowTextarea] = useState(false);
  const [clipboardError, setClipboardError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // If the assistant already parsed a recipe and stored it, skip the launcher
  // and go directly to the form
  useEffect(() => {
    const pending = readPendingRecipe();
    if (pending && (pending.title || pending.ingredients)) {
      navigate('/recipe/new/form');
    }
  }, [navigate]);

  // Focus textarea when it becomes visible
  useEffect(() => {
    if (showTextarea && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showTextarea]);

  // Handle paste from clipboard with proper error handling
  const handlePaste = async () => {
    setClipboardError(null);
    
    // Check if clipboard API is available
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      // Fallback: show textarea for manual paste
      setShowTextarea(true);
      setClipboardError('Clipboard access not available. Please paste manually below.');
      return;
    }

    try {
      const text = await navigator.clipboard.readText();
      if (text && text.trim()) {
        setPastedText(text);
        setShowTextarea(true);
        // Auto-parse if text is substantial
        if (text.trim().length >= 50) {
          handleParseRecipe(text);
        }
      } else {
        // Empty clipboard - show textarea
        setShowTextarea(true);
        setClipboardError('Clipboard is empty. Paste your recipe below.');
      }
    } catch {
      // Permission denied or other error - show textarea for manual paste
      setShowTextarea(true);
      setClipboardError('Could not access clipboard. Please paste manually below (Ctrl+V / Cmd+V).');
    }
  };

  // Handle clicking the type area to show textarea
  const handleShowTextarea = () => {
    setShowTextarea(true);
    setClipboardError(null);
  };

  // Check if file is acceptable
  const isAcceptableFile = (file: File): boolean => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type.toLowerCase();
    
    return ACCEPTED_EXTENSIONS.includes(extension) || 
           ACCEPTED_FILE_TYPES.includes(mimeType) ||
           mimeType.startsWith('text/');
  };

  // Read file content
  const readFileContent = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        if (text) {
          resolve(text);
        } else {
          reject(new Error('File is empty'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Handle file drop
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setParseError(null);
    
    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!isAcceptableFile(file)) {
      setParseError(`Unsupported file type. Please use ${ACCEPTED_DISPLAY}.`);
      return;
    }

    try {
      const text = await readFileContent(file);
      setPastedText(text);
      setShowTextarea(true);
      handleParseRecipe(text);
    } catch {
      setParseError('Failed to read file. Please try again or paste the content manually.');
    }
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Parse recipe with AI
  const handleParseRecipe = async (text: string) => {
    if (!text.trim() || text.trim().length < 20) {
      setParseError('Please provide more recipe content to parse.');
      return;
    }

    setViewState('parsing');
    setParseError(null);

    try {
      const result = await handleRecipeCreationRequest(text);
      
      if (result.success && result.data?.formData) {
        setParsedRecipe(result.data.formData);
        setViewState('preview');
      } else {
        setParseError(result.error || 'Failed to parse recipe. Please try again or enter manually.');
        setViewState('launcher');
      }
    } catch {
      setParseError('An unexpected error occurred. Please try again.');
      setViewState('launcher');
    }
  };

  // Handle textarea submit
  const handleTextareaSubmit = () => {
    if (pastedText.trim().length >= 20) {
      handleParseRecipe(pastedText);
    }
  };

  // Handle file input change
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so the same file can be selected again
    e.target.value = '';

    if (!isAcceptableFile(file)) {
      setParseError(`Unsupported file type "${file.name}". Please use ${ACCEPTED_DISPLAY}.`);
      return;
    }

    try {
      const text = await readFileContent(file);
      setPastedText(text);
      setShowTextarea(true);
      handleParseRecipe(text);
    } catch {
      setParseError('Failed to read file. Please try again or paste the content manually.');
    }
  };

  // Navigate to manual form
  const handleStartFromScratch = () => {
    navigate('/recipe/new/form');
  };

  // Navigate to studio
  const handleOpenStudio = () => {
    navigate('/studio');
  };

  // Save parsed recipe directly (with optional updated data including image)
  const handleSaveDirectly = (updatedRecipe?: Partial<RecipeFormData>) => {
    const recipeToSave = updatedRecipe || parsedRecipe;
    if (recipeToSave) {
      writePendingRecipe(recipeToSave);
      navigate('/recipe/new/form');
    }
  };

  // Edit parsed recipe
  const handleEditParsed = () => {
    if (parsedRecipe) {
      writePendingRecipe(parsedRecipe);
      navigate('/recipe/new/form');
    }
  };

  // Reset to launcher
  const handleReset = () => {
    setPastedText('');
    setParsedRecipe(null);
    setParseError(null);
    setClipboardError(null);
    setShowTextarea(false);
    setViewState('launcher');
  };

  // Render parsing progress
  if (viewState === 'parsing') {
    return <RecipeParsingProgress />;
  }

  // Render preview
  if (viewState === 'preview' && parsedRecipe) {
    return (
      <ParsedRecipePreview
        recipe={parsedRecipe}
        onSave={handleSaveDirectly}
        onEdit={handleEditParsed}
        onBack={handleReset}
      />
    );
  }

  // Main launcher view
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="container mx-auto px-4 py-4 max-w-3xl">
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <img
              src="/Cheflab.png"
              alt="ChefLab"
              className="h-6 w-auto dark:hidden"
            />
            <img
              src="/Cheflab_b.png"
              alt="ChefLab"
              className="h-6 w-auto hidden dark:block"
            />
          </div>
        </div>

        {/* Title - more compact */}
        <div className="text-center mb-5">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
            Add a New Recipe
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Choose how you'd like to get started
          </p>
        </div>

        {/* Error Message */}
        {parseError && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-start gap-2 text-sm">
            <span className="text-red-500">⚠️</span>
            <div className="flex-1">
              <p className="font-medium">Couldn't parse that recipe</p>
              <p className="text-xs opacity-80">{parseError}</p>
            </div>
            <button onClick={() => setParseError(null)} className="text-red-500 hover:text-red-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Option 1: Paste a Recipe (Primary) */}
        <Card className="mb-3 overflow-hidden border-0 shadow-lg bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm ring-1 ring-purple-200/50 dark:ring-purple-700/30">
          <CardContent className="p-4 md:p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 text-white">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Paste a Recipe
                </h2>
                <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                  Most popular • AI-powered
                </p>
              </div>
            </div>

            {/* Drop Zone - Compact */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`
                relative border-2 border-dashed rounded-lg p-4 transition-all duration-200
                ${isDragOver 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500'
                }
              `}
            >
              {/* Clipboard error/info message */}
              {clipboardError && !pastedText && (
                <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">{clipboardError}</p>
                </div>
              )}

              {/* Show textarea when we have text OR when user triggered manual paste mode */}
              {(pastedText || showTextarea) ? (
                <div className="space-y-3">
                  <Textarea
                    ref={textareaRef}
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    onPaste={(e) => {
                      // Handle paste event directly in textarea
                      const text = e.clipboardData?.getData('text');
                      if (text) {
                        e.preventDefault();
                        setPastedText(text);
                        setClipboardError(null);
                      }
                    }}
                    placeholder="Paste your recipe here (Ctrl+V / Cmd+V)..."
                    className="min-h-[120px] resize-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    autoFocus={showTextarea && !pastedText}
                  />
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handleReset}
                      className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Clear
                    </button>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="border-gray-300 dark:border-gray-600"
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        File
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleTextareaSubmit}
                        disabled={pastedText.trim().length < 20}
                        className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-md disabled:opacity-50"
                      >
                        <Wand2 className="h-3 w-3 mr-1" />
                        Parse Recipe
                      </Button>
                    </div>
                  </div>
                  {pastedText.trim().length > 0 && pastedText.trim().length < 20 && (
                    <p className="text-xs text-gray-400 text-center">
                      Need at least 20 characters to parse ({pastedText.trim().length}/20)
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-3">
                  {/* Drag indicator when dragging */}
                  {isDragOver ? (
                    <div className="py-4">
                      <Upload className="h-8 w-8 text-purple-500 mx-auto mb-2 animate-bounce" />
                      <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
                        Drop your file here!
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-center gap-2 mb-3">
                        <Button
                          size="sm"
                          onClick={handlePaste}
                          className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white shadow-md"
                        >
                          <ClipboardPaste className="h-3 w-3 mr-1" />
                          Paste from Clipboard
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Upload File
                        </Button>
                      </div>
                      
                      {/* Click to type option */}
                      <button
                        onClick={handleShowTextarea}
                        className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline underline-offset-2 mb-2 block mx-auto"
                      >
                        or click here to type/paste manually
                      </button>
                      
                      {/* File format info */}
                      <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1">
                        <p>Drag & drop a file, or paste from any website</p>
                        <p className="text-[10px] opacity-75">
                          Accepts: {ACCEPTED_DISPLAY}
                        </p>
                      </div>
                    </>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={[...ACCEPTED_EXTENSIONS, ...ACCEPTED_FILE_TYPES].join(',')}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Option 2: AI Studio */}
        <Card 
          className="mb-3 group cursor-pointer border-0 shadow-md bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm ring-1 ring-gray-200/50 dark:ring-gray-700/50 hover:shadow-lg hover:ring-emerald-300 dark:hover:ring-emerald-600 transition-all duration-200"
          onClick={handleOpenStudio}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 text-white shrink-0">
                <Wand2 className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  Create with AI Studio
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Tell me what to cook and AI will create a complete recipe
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-emerald-500 group-hover:translate-x-1 transition-all shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Option 3: Manual Entry (Last) */}
        <Card 
          className="group cursor-pointer border-0 shadow-md bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm ring-1 ring-gray-200/50 dark:ring-gray-700/50 hover:shadow-lg hover:ring-gray-300 dark:hover:ring-gray-600 transition-all duration-200"
          onClick={handleStartFromScratch}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 shrink-0">
                <PenLine className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors">
                  Start from Scratch
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Write your own recipe manually with full control
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all shrink-0" />
            </div>
          </CardContent>
        </Card>

        {/* Footer hint - subtle */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-4">
          Pro tip: Copy a recipe from any website and paste it above — AI does the rest! ✨
        </p>
      </div>
    </div>
  );
};

export default RecipeCreationLauncher;
